import { supabase } from '@/lib/supabaseClient';
import { logger } from '@/utils/logger';

/**
 * This function fixes the "packed" status issue by creating a database function
 * that handles the type casting properly.
 */
export async function setupDatabaseFix() {
  try {
    // Check if the function already exists
    const { data: exists, error: checkError } = await supabase.rpc('check_function_exists', {
      function_name: 'fix_status_packed'
    });
    
    if (checkError) {
      logger.debug('Could not check if function exists, attempting to create anyway');
    } else if (exists) {
      logger.debug('Function already exists');
      return { success: true, message: 'Function already exists' };
    }
    
    // Create a function that can properly handle the enum type conversion
    const { error } = await supabase.rpc('exec_sql', {
      sql: `
        CREATE OR REPLACE FUNCTION fix_status_packed(p_order_id UUID)
        RETURNS BOOLEAN AS $$
        BEGIN
          -- Use explicit type casting to ensure enum type compatibility
          UPDATE orders SET status = 'packed'::order_status WHERE id = p_order_id;
          RETURN FOUND;
        END;
        $$ LANGUAGE plpgsql;
      `
    });
    
    if (error) {
      logger.error('Error creating function:', error);
      return { success: false, error };
    }
    
    logger.debug('Successfully created database function to fix packed status');
    return { success: true };
  } catch (error) {
    logger.error('Error setting up database fix:', error);
    return { success: false, error };
  }
}

/**
 * Update an order status to "packed" using a character-by-character approach
 * to ensure no spaces or other characters are introduced
 */
export async function fixOrderPackedStatus(orderId: string) {
  try {
    logger.debug(`Attempting to fix status for order ${orderId} to "packed"`);
    
    // Create the string character by character to avoid any encoding issues
    const p = String.fromCharCode(112); // p
    const a = String.fromCharCode(97);  // a
    const c = String.fromCharCode(99);  // c
    const k = String.fromCharCode(107); // k
    const e = String.fromCharCode(101); // e
    const d = String.fromCharCode(100); // d
    
    // Combine characters directly
    const packedStatus = p + a + c + k + e + d;
    
    logger.debug(`Using constructed 'packed' status: "${packedStatus}"`);
    logger.debug('Character codes:', [...packedStatus].map(char => char.charCodeAt(0)));
    logger.debug('String length:', packedStatus.length);
    
    // Use a direct update with the carefully constructed string
    const { error } = await supabase
      .from('orders')
      .update({ status: packedStatus })
      .eq('id', orderId);
      
    if (error) {
      logger.error('Direct status update failed:', error);
      
      // If it still fails, try one more approach using a different method
      logger.debug('Trying alternate method with SQL query');
      
      try {
        // Try a SQL query as a last resort
        const { error: sqlError } = await supabase.rpc('exec_sql', {
          sql: `UPDATE orders SET status = 'packed'::order_status WHERE id = '${orderId}'`
        });
        
        if (sqlError) {
          logger.error('SQL update failed:', sqlError);
          return { success: false, error: sqlError };
        }
        
        logger.debug('SQL update succeeded');
        return { success: true, method: 'sql' };
      } catch (sqlError) {
        logger.error('SQL error:', sqlError);
        return { success: false, error: sqlError };
      }
    }
    
    logger.debug('Direct status update succeeded');
    return { success: true, method: 'direct' };
  } catch (error) {
    logger.error('Error fixing order status:', error);
    return { success: false, error };
  }
} 